<script>
import {computed} from "vue";
import {useCartStore} from "@js/store/CartStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import Tooltip from "@js/components/Tooltip.vue";
import IconBan from "@js/components/Icons/IconBan.vue";

export default {
    name: "ProductPriceConfigurator",
    components: {Tooltip, NumberFormatComponent, IconBan},
    props: {
        productId: {type: [Number, String], required: true},
        compact: {type: Boolean, default: false},
    },
    setup(props) {
        const cartStore = useCartStore();

        const product = computed(() => cartStore.productOf(props.productId));
        const prices = computed(() => product.value?.prices || []);
        const currentPrice = computed(() => cartStore.currentPriceEntry(props.productId));
        const specificationGroups = computed(() => cartStore.specificationGroups(props.productId));
        const currentUnit = computed(() => cartStore.unitLabel(props.productId));
        const currentPriceValue = computed(() => cartStore.priceActive(props.productId));

        const hasPrice = computed(() => {
            return currentPriceValue.value !== null
                && currentPriceValue.value !== undefined
                && currentPriceValue.value !== ''
                && !Number.isNaN(Number(currentPriceValue.value));
        });

        const selectedValueId = (typeId) => cartStore.selectedSpecValueId(props.productId, typeId);

        const selectPrice = (priceId) => {
            cartStore.setPrice(props.productId, priceId);
        };

        const selectSpecValue = (typeId, valueId) => {
            cartStore.setSpecificationValue(props.productId, typeId, valueId);
        };

        const priceButtonLabel = (price) => {
            return price?.price_type?.short_title || price?.price_type?.title || 'Цена';
        };

        const specImage = (value) => value?.image_url || null;
        const hasSpecImage = (value) => !!value?.image_url;

        const isSelected = (typeId, valueId) => {
            return Number(selectedValueId(typeId)) === Number(valueId);
        };

        const isUnselected = (typeId) => {
            const selected = selectedValueId(typeId);
            return selected === null || selected === undefined || selected === '';
        };

        return {
            prices,
            currentPrice,
            specificationGroups,
            currentUnit,
            currentPriceValue,
            hasPrice,
            selectedValueId,
            selectPrice,
            selectSpecValue,
            priceButtonLabel,
            specImage,
            hasSpecImage,
            isSelected,
            isUnselected,
        };
    }
};
</script>

<template>
    <div class="flex flex-col gap-2.5">
        <div
            :class="compact
                ? 'flex flex-col gap-2.5'
                : 'flex flex-wrap gap-y-2.5 gap-x-5'
            "
        >
            <div v-if="prices.length > 1" class="flex flex-col gap-1.5">
                <div class="text-xs font-semibold leading-none text-gray-700">
                    Тип цены:
                </div>

                <div class="flex flex-wrap gap-1.5">
                    <button
                        v-for="price in prices"
                        :key="price.id"
                        type="button"
                        @click="selectPrice(price.id)"
                        class="inline-flex min-h-[30px] items-center justify-center rounded-lg border px-2.5 py-1 text-[11px] font-semibold leading-none transition uppercase"
                        :class="[
                            Number(currentPrice?.id) === Number(price.id)
                                ? 'border-baseColor bg-baseColor text-black'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        ]"
                    >
                        {{ priceButtonLabel(price) }}
                    </button>
                </div>
            </div>

            <div
                v-for="group in specificationGroups"
                :key="group.type_id"
                class="flex flex-col gap-1.5"
            >
                <div class="text-xs font-semibold leading-none text-gray-700">
                    {{ group.type_name }}:
                </div>

                <div class="flex flex-wrap gap-1.5">
                    <button
                        type="button"
                        @click="selectSpecValue(group.type_id, null)"
                        class="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg border transition"
                        :class="[
                            isUnselected(group.type_id)
                                ? 'border-baseColor bg-baseColor text-black'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        ]"
                        title="Без выбора"
                    >
                        <IconBan class="h-4 w-4"/>
                    </button>

                    <button
                        v-for="value in group.values"
                        :key="value.id"
                        type="button"
                        @click="selectSpecValue(group.type_id, value.id)"
                        class="inline-flex items-center justify-center rounded-lg border transition overflow-hidden"
                        :class="[
                            isSelected(group.type_id, value.id)
                                ? 'border-baseColor bg-baseColor text-black'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400',
                            hasSpecImage(value) ? 'h-[30px] w-[30px]' : 'min-h-[30px] px-2 py-1'
                        ]"
                    >
                        <span
                            v-if="!hasSpecImage(value)"
                            class="text-xs font-semibold leading-none"
                        >
                            {{ value.value }}
                        </span>

                        <Tooltip v-else :text="value.value">
                            <img
                                :src="specImage(value)"
                                :alt="value.value"
                                class="h-full w-full object-contain"
                            >
                        </Tooltip>
                    </button>
                </div>
            </div>
        </div>

        <div
            class="text-sm text-gray-700 col-span-full"
            :class="compact || prices.length <= 1 ? 'pt-1' : 'mt-1 border-t border-gray-100 pt-2'"
        >
            <span class="font-medium">Цена: </span>

            <template v-if="hasPrice && currentPriceValue > 0">
                <NumberFormatComponent
                    :number="currentPriceValue"
                    append_text=" ₽"
                    class="text-base md:text-lg font-semibold text-black"
                />
                <span class="font-medium text-sm">/{{ currentUnit }}</span>
            </template>

            <span
                v-else
                class="text-base md:text-lg font-semibold text-black"
            >
                договорная
            </span>
        </div>
    </div>
</template>
